import { noop, isNil, isEmpty } from 'lodash/fp'
import { useEffect, useRef, useState } from 'react'
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { Box, Flex } from 'rebass'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import waterMaskUrl from 'assets/images/water-mask.png'
import {
  removeFile,
  deletePhoto,
  browsePhoto,
  capturePhoto,
  previewPhoto,
  removeError,
} from 'e-submission/apps/actions'
import { getFileWithName, isPDFFileType, getErrorByTypeName } from 'e-submission/apps/selectors'
import Card from 'e-submission/components/Card/index'
import Dialog from 'e-submission/components/Dialog'
import FormEngine from 'e-submission/components/FormEngine'
import Icon, { getIcon } from 'e-submission/components/Icon'
import { isBlob } from 'e-submission/utils/pattern'
import { resolveImageFile } from 'e-submission/utils/promise'
import viewerUrl from 'file-loader?name=pdf-viewer/web/viewer.html&emitFile=false!e-submission/pdf-viewer/web/viewer.html'

export const WatermarkModal = styled(Dialog)`
  .modal-content {
    height: 100vh;
  }
  .modal-title {
    display: flex;
    flex: 1 0 100%;
    justify-content: space-between;
    align-items: center;
    button {
      padding: 5px 10px;
    }
  }
  .modal-body {
    display: flex;
    flex-direction: column;
    padding: 0 !important;
    iframe {
      flex: 1;
    }
  }
  .modal-footer {
    justify-content: space-between;
  }
  .formEngine {
    min-height: 260px;
  }
`
const onLoadIframe = (evt) => {
  const iframe = evt.target
  const iframeDocument = iframe.contentWindow.document
  const toolbar = iframeDocument.querySelector('.toolbar')
  const main = iframeDocument.querySelector('#mainContainer')
  const content = iframeDocument.querySelector('#viewerContainer')
  const customStyle = document.createElement('style')
  customStyle.type = 'text/css'
  customStyle.textContent = `
    .canvasWrapper:before {
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-image: url(${waterMaskUrl});
      background-size: 200px;
      z-index: 10;
    }
  `
  iframeDocument.head.appendChild(customStyle)
  toolbar.style.visibility = 'hidden'
  content.style.top = 0
}

export const WithWatermark = styled(({ image, ...props }) => <Flex {...props} />)`
  position: relative;
  overflow: hidden;
  background-color: black;
  background-image: url('${({ image }) => image}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  &:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-image: url(${waterMaskUrl});
    background-size: 200px;
    background-attachment: fixed;
  }
`

export const MoreInfoPhotoCard = ({
  editable = false,
  disabled,
  isOk,
  sections,
  name,
  accept = 'image/x-png,image/jpeg,application/pdf',
  checked,
  readOnly,
  setFileProps,
  validateFile,
  CheckedComponent,
  NotCheckedComponent,
  isBrowseFile = !(typeof window === 'object' && !isEmpty(window.cordova)),
  ...props
}) => {
  const stateApp = useSelector((state) => state.apps)
  const url = getFileWithName(stateApp, name)
  const isPDF = isPDFFileType(stateApp, name)
  const isUploadError = !isNil(getErrorByTypeName(state, name))
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef(null)

  const setRef = node => { ref.current = node; }
  const getRef = () => ref.current
  const clearFile = () => {
    if (ref.current) {
      ref.current.value = ''
    }
  }

  const openModal = () => setIsOpen(true)
  const closeModal = () => {
    setIsOpen(false)
    clearFile()
  }

  const onDelete = () => {
    closeModal()
    dispatch(deletePhoto(name))
    if (!editable) {
      dispatch(removeFile(name))
    }
  }
  const onPreview = () => {
    if (checked) {
      openModal()
      dispatch(previewPhoto(name))
    }
  }
  const onClose = () => {
    closeModal()
    if (!editable) {
      dispatch(removeFile(name))
    }
  }
  const onOk = () => {
    let isOk = props.onOk()
    if (isOk !== false) {
      closeModal()
      if (!editable) {
        dispatch(removeFile(name))
      }
    }
  }
  const onOpen = async (event) => {
    dispatch(removeError(name))
    props.onOpen()

    const isShowPreview = !isNil(sections)

    if (isShowPreview) {
      openModal()
    }

    if (isBrowseFile) {
      const file = event.target.files[0]

      if (isBlob(file) && file) {
        const format = await resolveImageFile(file)

        validateFile()
        dispatch(browsePhoto(name, file, isShowPreview, undefined, format))
      } else {
        validateFile()
      }
    } else {
      validateFile()
      dispatch(capturePhoto(name, isShowPreview))
    }

    if (editable) {
      clearFile()
    }
  }

  const browseFile = () => getRef().click()
  const InfoCard = styled(({ className, children, description, icon, ...props }) => (
    <Card className={className} w={1} column {...props}>
      <Flex align="center">
        <Flex className="icon-left">{icon && getIcon(icon)}</Flex>
        <Box flex="auto">
          <strong>{description}</strong>
        </Box>
        <Icon.arrowRight className="arrowRight" />
      </Flex>
    </Card>
  ))`
    padding: 10px 20px;
    &:hover {
      cursor: pointer;
    }
    .arrowRight {
      color: ${({ theme }) => theme.variables['$brand-primary']};
    }
    .icon-left svg {
      max-width: 22px;
      max-height: 30px;
      margin-left: -3px;
      margin-right: 5px;
    }
  `

  useEffect(() => {
    if (isOpen && isUploadError === true) {
      onClose()
    }
  }, [isUploadError])

  return (
    <div>
      <WatermarkModal isOpen={isOpen} toggle={onClose}>
        <ModalHeader tag="div">
          <div className="btn-left" onClick={onClose}>
            <Icon.arrowLeft className="icon" />
          </div>
          <div>{props.title}</div>
          <div />
        </ModalHeader>
        <ModalBody>
          {!isPDF ? (
            <WithWatermark flex="1 1 100%" align="center" image={url} />
          ) : (
            <iframe src={`${viewerUrl}?file=${url}`} onLoad={onLoadIframe} />
          )}
          {sections && (
            <div className="formEngine">
              <FormEngine sections={sections} />
            </div>
          )}
        </ModalBody>
        {readOnly ? null : (
          <ModalFooter>
            <Button color="primary" outline onClick={onDelete}>
              ลบ
            </Button>
            <Button color={!checked ? 'disabled' : 'primary'} onClick={onOk}>
              ตกลง
            </Button>
          </ModalFooter>
        )}
      </WatermarkModal>
      {checked ? (
        <InfoCard {...props} onClick={onPreview} />
      ) : (
        <div>
          <InfoCard {...props} onClick={() => !disabled && (isBrowseFile ? browseFile() : onOpen())} />
          {isBrowseFile && <input ref={setRef} type="file" onChange={onOpen} accept={accept} />}
        </div>
      )}
    </div>
  )
}

export default (MoreInfoPhotoCard)
